// API utilities and configuration

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiError, LoginResponse, User, PaginatedResponse } from '@/types';
import { criticalLogger } from '@/utils/logger';
import i18n from '@/i18n';

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
class TokenManager {
  private static readonly ACCESS_TOKEN_KEY = 'access_token';
  private static readonly REFRESH_TOKEN_KEY = 'refresh_token';

  static getAccessToken(): string | null {
    return sessionStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  static setAccessToken(token: string): void {
    sessionStorage.setItem(this.ACCESS_TOKEN_KEY, token);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  static setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  static clearTokens(): void {
    sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  static setTokens(accessToken: string, refreshToken: string): void {
    this.setAccessToken(accessToken);
    this.setRefreshToken(refreshToken);
  }
}

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = TokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = TokenManager.getRefreshToken();
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token } = response.data;
          TokenManager.setAccessToken(access_token);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        TokenManager.clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Generic API methods
class ApiService {
  static async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await apiClient.get(url, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  static async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await apiClient.post(url, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  static async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await apiClient.put(url, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  static async patch<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await apiClient.patch(url, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  static async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await apiClient.delete(url, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private static handleError(error: any): ApiError {
    // Log error for debugging
    criticalLogger.error('API Error:', error);
    
    // Helper function to extract error message from various formats
    const extractErrorMessage = (detail: any): string => {
      if (typeof detail === 'string') {
        return detail;
      }
      if (Array.isArray(detail)) {
        // Pydantic validation errors array
        return detail.map((err: any) => {
          if (typeof err === 'string') return err;
          if (err.msg) return err.msg;
          if (err.message) return err.message;
          return JSON.stringify(err);
        }).join(', ');
      }
      if (detail && typeof detail === 'object') {
        // Single error object
        if (detail.msg) return detail.msg;
        if (detail.message) return detail.message;
        if (detail.detail) return extractErrorMessage(detail.detail);
        // Try to stringify if it's an object
        return JSON.stringify(detail);
      }
      return i18n.t('commonErrors.unknown');
    };
    
    if (error.response) {
      // Server responded with error status
      const rawDetail = error.response.data?.detail || 
                       error.response.data?.message || 
                       i18n.t('commonErrors.serverError', { status: error.response.status });
      const detail = extractErrorMessage(rawDetail);
      criticalLogger.error('Response error:', {
        status: error.response.status,
        data: error.response.data,
        detail
      });
      return {
        detail: detail,
        status_code: error.response.status,
      };
    } else if (error.request) {
      // Request was made but no response received (proxy/socket timeout, CORS, offline)
      criticalLogger.error('No response received:', error.request, error.code, error.message);
      const timedOut =
        error.code === 'ECONNABORTED' ||
        (typeof error.message === 'string' && error.message.toLowerCase().includes('timeout'));
      return {
        detail: timedOut
          ? i18n.t('commonErrors.requestTimeout', {
              defaultValue:
                'Request timed out. AI generation can take several minutes — retry; if it persists, check the CRA proxy timeout / Softlavka AI latency.',
            })
          : i18n.t('commonErrors.noResponse'),
      };
    } else {
      // Something else happened
      criticalLogger.error('Request setup error:', error.message);
      return {
        detail: error.message || i18n.t('commonErrors.unknownOccurred'),
      };
    }
  }
}

// Specific API endpoints
export const authApi = {
  login: (credentials: { username: string; password: string }): Promise<LoginResponse> =>
    ApiService.post<LoginResponse>('/auth/login', credentials),
  
  logout: (refreshToken: string): Promise<void> =>
    ApiService.post<void>('/auth/logout', { refresh_token: refreshToken }),
  
  refreshToken: (refreshToken: string): Promise<{ access_token: string }> =>
    ApiService.post<{ access_token: string }>('/auth/refresh', { refresh_token: refreshToken }),
  
  /** 401 doesn't throw axios — no noise in console on expired session */
  getCurrentUser: async (): Promise<User | null> => {
    const response = await apiClient.get<User>('/auth/me', {
      validateStatus: (status) => status === 200 || status === 401,
    })
    if (response.status === 401) return null
    return response.data
  },
  
  changePassword: (data: { current_password: string; new_password: string }): Promise<void> =>
    ApiService.post<void>('/auth/change-password', data),
};

export const usersApi = {
  search: (params: any): Promise<PaginatedResponse<User>> =>
    ApiService.get<PaginatedResponse<User>>('/users/search', { params }),

  getById: (id: string, queryParams?: string): Promise<User> =>
    ApiService.get<User>(`/users/${id}${queryParams || ''}`),

  create: (data: any): Promise<User> =>
    ApiService.post<User>('/users/', data),

  update: (id: string, data: any): Promise<User> =>
    ApiService.put<User>(`/users/${id}`, data),

  delete: (id: string): Promise<void> =>
    ApiService.delete<void>(`/users/${id}`),

  changePassword: (
    userId: string,
    data: { current_password?: string; new_password: string }
  ): Promise<void> => ApiService.post<void>(`/users/${userId}/change-password`, data),
};

export interface DatabaseConnection {
  id: string;  // 32-character hex identifier
  name: string;
  db_type: 'sqlite' | 'postgresql' | 'mssql' | 'oracle';
  host?: string;
  port?: number;
  database?: string;
  username?: string;
  file_path?: string;
  connection_string?: string;
  options?: string;
  is_active: boolean;
  is_default: boolean;
  description?: string;
  enabled_modules?: string[];
  created_at: string;
  updated_at: string;
  created_by?: number;
}

export interface ConnectionCreate {
  name: string;
  db_type: 'sqlite' | 'postgresql' | 'mssql' | 'oracle';
  host?: string;
  port?: number;
  database?: string;
  username?: string;
  password?: string;
  file_path?: string;
  connection_string?: string;
  options?: string;
  is_active?: boolean;
  is_default?: boolean;
  description?: string;
  enabled_modules?: string[];
}

export interface AvailableModule {
  name: string;
  display_name: string;
  path: string;
  models_count: number;
  is_installed: boolean;
  is_available: boolean;
}

export interface AvailableModulesResponse {
  modules: AvailableModule[];
  total: number;
}

export interface ConnectionTestResponse {
  success: boolean;
  message: string;
  details?: any;
}

// ── User Preferences API (per-connection) ──
export const preferencesApi = {
  getAll: (connectionId: string): Promise<{ user_id: string; data: Record<string, unknown> }> =>
    ApiService.get('/v1/preferences/', { params: { connection_id: connectionId } }),
  update: (connectionId: string, data: Record<string, unknown>): Promise<{ user_id: string; data: Record<string, unknown> }> =>
    ApiService.put('/v1/preferences/', { data }, { params: { connection_id: connectionId } }),
  getKey: async (
    connectionId: string,
    key: string,
  ): Promise<{ key: string; value: unknown } | null> => {
    const response = await apiClient.get<{ key: string; value: unknown }>(
      `/v1/preferences/${key}`,
      {
        params: { connection_id: connectionId },
        validateStatus: (status) => status === 200 || status === 404 || status === 503,
      },
    );
    if (response.status === 200) return response.data;
    return null;
  },
  setKey: (connectionId: string, key: string, value: unknown): Promise<{ user_id: string; data: Record<string, unknown> }> =>
    ApiService.put(`/v1/preferences/${key}`, { value }, { params: { connection_id: connectionId } }),
};

export const connectionsApi = {
  getAll: (activeOnly?: boolean): Promise<DatabaseConnection[]> =>
    ApiService.get<DatabaseConnection[]>('/connections/', { params: { active_only: activeOnly } }),
  
  getById: (id: string): Promise<DatabaseConnection> =>
    ApiService.get<DatabaseConnection>(`/connections/${id}`),
  
  getDefault: (): Promise<DatabaseConnection> =>
    ApiService.get<DatabaseConnection>('/connections/default/active'),
  
  create: (data: ConnectionCreate): Promise<DatabaseConnection> =>
    ApiService.post<DatabaseConnection>('/connections/', data),
  
  update: (id: string, data: Partial<ConnectionCreate>): Promise<DatabaseConnection> =>
    ApiService.put<DatabaseConnection>(`/connections/${id}`, data),
  
  delete: (id: string): Promise<void> =>
    ApiService.delete<void>(`/connections/${id}`),
  
  test: (data: { connection_id?: string; connection_data?: ConnectionCreate }): Promise<ConnectionTestResponse> =>
    ApiService.post<ConnectionTestResponse>('/connections/test', data),
  
  getAvailableModules: (): Promise<AvailableModulesResponse> =>
    ApiService.get<AvailableModulesResponse>('/connections/modules/available'),
  installModule: (moduleName: string): Promise<{ message: string; module_name: string; is_installed: boolean }> =>
    ApiService.post<{ message: string; module_name: string; is_installed: boolean }>(`/connections/modules/${moduleName}/install`),
  enableModuleForConnection: (connectionId: string, moduleName: string): Promise<{ message: string; enabled_modules: string[] }> =>
    ApiService.post<{ message: string; enabled_modules: string[] }>(`/connections/${connectionId}/modules/${moduleName}/enable`),
  disableModuleForConnection: (connectionId: string, moduleName: string): Promise<{ message: string; enabled_modules: string[] }> =>
    ApiService.delete<{ message: string; enabled_modules: string[] }>(`/connections/${connectionId}/modules/${moduleName}/enable`),
};

export const designerApi = {
  // Legacy endpoints
  generateEntity: (data: { description: string; use_ai: boolean }) =>
    ApiService.post('/designer/generate', data),
  
  getEntities: (includeSystem = true) =>
    ApiService.get('/designer/entities/', { params: { include_system: includeSystem } }),
  
  getEntity: (id: number) =>
    ApiService.get(`/designer/entities/${id}`),
  
  createEntity: (data: any) =>
    ApiService.post('/designer/entities/', data),
  
  updateEntity: (id: number, data: any) =>
    ApiService.put(`/designer/entities/${id}`, data),
  
  deleteEntity: (id: number) =>
    ApiService.delete(`/designer/entities/${id}`),
  
  getEntityInstances: (entityId: number, params?: any) =>
    ApiService.get(`/designer/entities/${entityId}/instances/`, { params }),
  
  createEntityInstance: (entityId: number, data: any) =>
    ApiService.post(`/designer/entities/${entityId}/instances/`, data),
  
  updateEntityInstance: (instanceId: number, data: any) =>
    ApiService.put(`/designer/instances/${instanceId}`, data),
  
  deleteEntityInstance: (instanceId: number) =>
    ApiService.delete(`/designer/instances/${instanceId}`),
  
  // Extended Designer endpoints
  // Schema Designer
  createSchema: (data: any) =>
    ApiService.post('/v1/designer/schemas/', data),
  
  getSchemas: (includeInactive = false) =>
    ApiService.get('/v1/designer/schemas/', { params: { include_inactive: includeInactive } }),
  
  getSchema: (id: number) =>
    ApiService.get(`/v1/designer/schemas/${id}`),
  
  getSchemaFull: (id: number) =>
    ApiService.get(`/v1/designer/schemas/${id}/full`),
  
  updateSchema: (id: number, data: any) =>
    ApiService.put(`/v1/designer/schemas/${id}`, data),
  
  deleteSchema: (id: number) =>
    ApiService.delete(`/v1/designer/schemas/${id}`),
  
  exportSchema: (id: number) =>
    ApiService.post(`/v1/designer/schemas/${id}/export`),
  
  importSchema: (data: any) =>
    ApiService.post('/v1/designer/schemas/import', data),
  
  // Form Designer
  createForm: (data: any) =>
    ApiService.post('/v1/designer/forms/', data),
  
  getFormsByEntity: (entityId: number) =>
    ApiService.get(`/v1/designer/forms/entity/${entityId}`),
  
  getForm: (id: number) =>
    ApiService.get(`/v1/designer/forms/${id}`),
  
  getFormCode: (id: number) =>
    ApiService.get(`/v1/designer/forms/${id}/code`),
  
  // Business Logic Designer
  createLogic: (data: any): Promise<any> =>
    ApiService.post<any>('/v1/designer/logic/', data),
  
  generateLogicWithAI: (data: any): Promise<{ code: string }> =>
    ApiService.post<{ code: string }>('/v1/designer/logic/generate', data),
  
  getLogicByEntity: (entityId: number): Promise<any[]> =>
    ApiService.get<any[]>(`/v1/designer/logic/entity/${entityId}`),
  
  getLogic: (id: number): Promise<any> =>
    ApiService.get<any>(`/v1/designer/logic/${id}`),
  
  testLogic: (id: number, testData: any): Promise<any> =>
    ApiService.post<any>(`/v1/designer/logic/${id}/test`, testData),
  
  // CRUD Generation
  generateCRUD: (data: any) =>
    ApiService.post('/v1/designer/crud/generate', data),
  
  downloadGeneratedCRUD: (data: any) =>
    ApiService.post('/v1/designer/crud/generate/download', data),

  publishModule: (
    moduleKey: string,
    body: {
      connection_id: string;
      version: string;
      token: string;
      display_name?: string;
      description?: string;
    },
  ) =>
    ApiService.post<{ module_key: string; version: string; sha256: string; stamped?: boolean }>(
      `/v1/designer/config/modules/${encodeURIComponent(moduleKey)}/publish`,
      body,
    ),

  getLocalModules: (connectionId: string) =>
    ApiService.get<LocalModulesResponse>('/v1/designer/config/modules/local', {
      params: { connection_id: connectionId },
    }),

  assignModuleObjects: (body: {
    connection_id: string;
    module_key: string;
    add?: string[];
    remove?: string[];
  }) =>
    ApiService.post<{ module_key: string; added: string[]; removed: string[] }>(
      '/v1/designer/config/modules/assign',
      body,
    ),
};

export interface LocalModule {
  module_key: string;
  object_names: string[];
}

export interface LocalModulesResponse {
  modules: LocalModule[];
  unassigned: string[];
}

export type CatalogModuleStatus = 'not_installed' | 'installed' | 'update_available';

export interface CatalogModule {
  module_key: string;
  display_name: string;
  description?: string;
  version: string;
  installed_version: string | null;
  status: CatalogModuleStatus;
  dependencies?: string[];
  bundles?: Record<string, number>;
  has_demo?: boolean;
}

export interface CatalogBundle {
  bundle_key: string;
  display_name: string;
  description: string;
  module_keys: string[];
  category: string;
}

export interface MarketplaceCatalog {
  modules: CatalogModule[];
  bundles: CatalogBundle[];
  registry_unreachable: boolean;
}

// Applying a module's predefined data runs one record at a time; the NPO
// chart alone is 1001 rows, which exceeds the shared 30s client timeout.
const MODULE_INSTALL_TIMEOUT_MS = 600000;

export const marketplaceApi = {
  getCatalog: (connectionId: string) =>
    ApiService.get<MarketplaceCatalog>('/marketplace/catalog', {
      params: { connection_id: connectionId },
    }),

  installModule: (key: string, connectionId: string) =>
    ApiService.post(`/marketplace/install/${key}`, {}, {
      params: { connection_id: connectionId },
      timeout: MODULE_INSTALL_TIMEOUT_MS,
    }),

  updateModule: (key: string, connectionId: string) =>
    ApiService.post(`/marketplace/update/${key}`, {}, {
      params: { connection_id: connectionId },
      timeout: MODULE_INSTALL_TIMEOUT_MS,
    }),

  uninstallModule: (key: string, connectionId: string) =>
    ApiService.delete<{ removed_objects?: string[] }>(`/marketplace/installer/${key}`, {
      params: { connection_id: connectionId },
      timeout: MODULE_INSTALL_TIMEOUT_MS,
    }),

  loadDemoData: (key: string, connectionId: string) =>
    ApiService.post<{ loaded: Record<string, number>; skipped: Record<string, number>; errors: string[] }>(
      `/marketplace/demo/${key}`,
      {},
      { params: { connection_id: connectionId }, timeout: MODULE_INSTALL_TIMEOUT_MS },
    ),

  installBundle: (bundleKey: string, connectionId: string) =>
    ApiService.post<{ installed: string[]; skipped: string[]; errors: string[] }>(
      `/marketplace/bundles/${bundleKey}/install`,
      {},
      { params: { connection_id: connectionId }, timeout: MODULE_INSTALL_TIMEOUT_MS },
    ),

  installBatch: (moduleKeys: string[], connectionId: string) =>
    ApiService.post<{ installed: string[]; skipped: string[]; errors: string[] }>(
      '/marketplace/install-batch',
      { module_keys: moduleKeys, connection_id: connectionId },
      { timeout: MODULE_INSTALL_TIMEOUT_MS },
    ),

  installPreview: (moduleKeys: string[], connectionId: string) =>
    ApiService.post<{
      order: string[];
      module_keys: string[];
      will_install: string[];
      already_installed: string[];
    }>('/marketplace/install-preview', {
      module_keys: moduleKeys,
      connection_id: connectionId,
    }),

  getInstalledModules: () =>
    ApiService.get('/marketplace/installed'),
};

export const licensingApi = {
  activate: (key: string, installId?: string) =>
    ApiService.post<{ valid: boolean; license_type?: string; organization_name?: string; valid_until?: string; ai_credits: number }>(
      '/licensing/activate',
      { key, install_id: installId }
    ),
  status: () =>
    ApiService.get<{ valid: boolean; license_type?: string; organization_name?: string; valid_until?: string; ai_credits: number }>(
      '/licensing/status'
    ),
};

export const printFormsApi = {
  // Layouts
  createLayout: (data: any) =>
    ApiService.post('/print-forms/print-layouts/', data),
  
  getLayoutsByEntity: (entityId: number, connectionId: string | number) =>
    ApiService.get(`/print-forms/print-layouts/entity/${entityId}`, {
      params: { connection_id: connectionId },
    }),
  
  checkEntityLayouts: (entityId: number, connectionId: number) =>
    ApiService.get(`/print-forms/print-layouts/entity/${entityId}/check`, {
      params: { connection_id: connectionId },
    }),
  
  getLayout: (layoutId: number, connectionId?: string) =>
    ApiService.get(`/print-forms/print-layouts/${layoutId}`, {
      params: connectionId ? { connection_id: connectionId } : undefined,
    }),

  /** Raw LayoutModel for EditorTemplates */
  getLayoutRaw: (layoutId: number, connectionId: string) =>
    ApiService.get(`/print-forms/layout/${layoutId}`, {
      params: { connection_id: connectionId },
    }),

  saveLayoutRaw: (layoutId: number, connectionId: string, layout: Record<string, unknown>) =>
    ApiService.post(`/print-forms/layout/${layoutId}`, layout, {
      params: { connection_id: connectionId },
    }),
  
  updateLayout: (layoutId: number, data: any, connectionId?: string) =>
    ApiService.put(`/print-forms/print-layouts/${layoutId}`, data, {
      params: connectionId ? { connection_id: connectionId } : undefined,
    }),
  
  deleteLayout: (layoutId: number, connectionId?: string) =>
    ApiService.delete(`/print-forms/print-layouts/${layoutId}`, {
      params: connectionId ? { connection_id: connectionId } : undefined,
    }),
  
  // Printing
  printDocument: (data: { layout_id: number; instance_id: number; format?: string; connection_id: string }) =>
    ApiService.post('/print-forms/print', data),
  
  printDocumentHtml: (layoutId: number, instanceId: number, connectionId: number) =>
    ApiService.get(`/print-forms/print/${layoutId}/${instanceId}`, {
      params: { connection_id: connectionId },
      responseType: 'text',
    }),

  // New Print API
  listLayouts: (connectionId: string, params?: { entity_id?: number; table_name?: string }) =>
    ApiService.get('/print/layouts', {
      params: { connection_id: connectionId, ...(params || {}) },
    }),

  preview: (data: { layout_id: number; instance_id: string; connection_id: string }) =>
    ApiService.post('/print/preview', data),

  pdf: (data: { layout_id: number; instance_id: string; connection_id: string }) =>
    ApiService.post('/print/pdf', data),

  /** Print from Designer objectLayoutItem.layoutSchema (saved in config tree) */
  previewDesigner: (data: {
    connection_id: string;
    table_name: string;
    instance_id: string;
    layout_node_id: string;
  }) => ApiService.post('/print/preview-designer', data),

  pdfDesigner: (data: {
    connection_id: string;
    table_name: string;
    instance_id: string;
    layout_node_id: string;
  }) => ApiService.post('/print/pdf-designer', data),

  xlsx: (data: { layout_id: number; instance_id: string; connection_id: string }) =>
    ApiService.post('/print/xlsx', data),

  // Server-side printing (CUPS / network printers)
  listPrinters: () => ApiService.get('/print/printers'),

  printServer: (data: {
    layout_id: number;
    instance_id: string;
    connection_id: string;
    printer: string;
    copies?: number;
  }) => ApiService.post('/print/print-server', data),

  printServerDesigner: (data: {
    connection_id: string;
    table_name: string;
    instance_id: string;
    layout_node_id: string;
    printer: string;
    copies?: number;
  }) => ApiService.post('/print/print-server-designer', data),

  // Print window (spreadsheet document model)
  spreadsheetDesigner: (data: {
    connection_id: string;
    table_name: string;
    instance_id: string;
    layout_node_id: string;
    locale?: string | null;
  }) => ApiService.post('/print/spreadsheet-designer', data),

  fillSchema: (data: {
    connection_id: string;
    table_name: string;
    instance_id: string;
    schema: unknown;
    locale?: string | null;
  }) => ApiService.post('/print/fill-schema', data),

  renderModel: (data: {
    model: unknown;
    format: 'html' | 'pdf';
    connection_id?: string;
    attachment_ids?: string[];
  }) => ApiService.post('/print/render-model', data),

  exportXlsx: (data: { model: unknown }) =>
    ApiService.post('/print/export-xlsx', data),

  printServerModel: (data: {
    model: unknown;
    printer: string;
    copies?: number;
    connection_id?: string;
    attachment_ids?: string[];
  }) => ApiService.post('/print/print-server-model', data),

  sendEmail: (data: {
    connection_id: string;
    model: unknown;
    attachment_ids: string[];
    to: string[];
    subject: string;
    body: string;
  }) => ApiService.post('/print/send-email', data),

  // Fields & assets
  getFields: (connectionId: string, tableName: string) =>
    ApiService.get('/print-forms/fields', {
      params: { connection_id: connectionId, table_name: tableName },
    }),

  uploadAsset: (data: FormData) =>
    ApiService.post('/print-forms/assets', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  getAssetUrl: (assetId: number, connectionId: string) =>
    `/api/print-forms/assets/${assetId}?connection_id=${encodeURIComponent(connectionId)}`,
};

export { TokenManager, ApiService };
export default apiClient;
